import { StatusCodes } from 'http-status-codes'

import { loginLink } from '../router'

import { makeEnvironmentModel } from './makeEnvironmentModel'
import { MeasurementUnitsService } from './MeasurementUnitsService'

interface FetchWithTimeoutAndAbortControllerOptions extends RequestInit {
  signal?: AbortController['signal'] | null
  timeoutMs?: number
}

export interface FetchModelEnvironment extends Pick<typeof window, 'fetch' | 'setTimeout' | 'clearTimeout'> {}

const defaultRequestInit = {
  credentials: 'include',
} as const

interface FetchResult {
  response?: Response
  error?: Error
  hasError: boolean
  didAbort: boolean
  didTimeout: boolean
}

interface FetchLoggingParams {
  fetchOperationName: string
  context?: Record<string, unknown>
}

interface HandleFailedResponseParams extends FetchLoggingParams {
  fetchResult: FetchResult
}

export interface FetchResponseError {
  message: string
  extra: string | null
}
interface LoadError {
  result?: never
  error: Error
}

interface LoadSuccess<T> {
  result: T
  error?: never
}

type LoadObjectResult<T> = LoadError | LoadSuccess<T>

export const FetchModel = makeEnvironmentModel<FetchModelEnvironment>('FetchModel')
  .views((_self) => ({
    get baseUrl(): string {
      // TODO: get this from the environment
      const { protocol, hostname } = window.location
      const apiHostname = hostname.includes('flytrex.com') ? `api-${hostname}` : hostname
      return `${protocol}//${apiHostname}:8000/api/v1/`
    },
  }))
  .actions((self) => ({
    // Based on https://stackoverflow.com/a/57888548/915875
    async _fetchWithTimeoutAndAbortSignal(
      url: RequestInfo,
      didTimeoutFlag: { didTimeout: boolean },
      { signal, timeoutMs = 20_000, ...options }: FetchWithTimeoutAndAbortControllerOptions = {},
    ): Promise<Response | Error> {
      const controller = new AbortController()
      const { globalEnvironment } = self
      const context = {
        url,
        signal,
        timeoutMs,
        options,
        controller,
      } as const
      console.info(`[${options.method ?? 'GET'}] '${url.toString()}'...`, { payload: options.body, ...context })
      try {
        const requestPromise = globalEnvironment.fetch(url, { signal: controller.signal, ...options })
        signal?.addEventListener('abort', () => controller.abort(), { once: true })
        const timeoutId = globalEnvironment.setTimeout(function abortFetchOnTimeoutHit() {
          console.warn(`Aborting request to '${url.toString()}' due to timeout hit of ${timeoutMs}ms.`, {
            requestPromise,
            ...context,
          })
          controller.abort()
          didTimeoutFlag.didTimeout = true
        }, timeoutMs)
        return requestPromise.finally(() => globalEnvironment.clearTimeout(timeoutId))
      } catch (ex) {
        return ex as Error
      }
    },
    _handleAbortOrNetworkFailure({
      fetchResult,
      fetchOperationName,
      context = {},
    }: HandleFailedResponseParams): boolean {
      const { didAbort, hasError, response } = fetchResult
      if (didAbort || hasError || !response) {
        if (didAbort) {
          console.info(`Aborted ${fetchOperationName}`, { fetchResult, ...context })
        }
        if (hasError) {
          console.warn(`Error ${fetchOperationName}`, { fetchResult, ...context })
        }
        return false
      }
      return true
    },
    async _handleResponsePayloadFailure({
      fetchResult,
      fetchOperationName,
      context = {},
    }: HandleFailedResponseParams): Promise<boolean> {
      const { response } = fetchResult
      if (!response) {
        return false
      }
      if (response.ok) {
        return true
      }
      if (response.status === StatusCodes.UNAUTHORIZED) {
        console.warn(
          `Failed ${fetchOperationName}, because the user is not authenticated. Redirecting to login page.`,
          { fetchResult, ...context },
        )
        loginLink.replace()
      } else {
        const { status, statusText } = response
        const responseText = await response.text()
        let responseJson = { message: '' }
        try {
          responseJson = JSON.parse(responseText) as FetchResponseError
        } catch {}
        console.warn(
          `Failed ${fetchOperationName} with status ${status}, statusText '${statusText}'.\n${responseJson.message}`,
          { responseText, responseJson, ...context, fetchResult },
        )
      }
      return false
    },
  }))
  .actions((self) => ({
    async fetch(
      url: RequestInfo,
      requestInit: FetchWithTimeoutAndAbortControllerOptions = defaultRequestInit,
    ): Promise<FetchResult> {
      let responseOrError: Response | Error | null = null
      const didTimeoutFlag = { didTimeout: false }
      if (requestInit.body && !requestInit.headers) {
        requestInit.headers = new Headers({ 'Content-Type': 'application/json' })
      }
      try {
        responseOrError = await self._fetchWithTimeoutAndAbortSignal(url, didTimeoutFlag, {
          ...defaultRequestInit,
          ...requestInit,
        })
      } catch (ex) {
        const context = { ex, url, requestInit, self } as const
        // https://stackoverflow.com/a/61113372/915875
        if ((ex as Error)?.message === 'Failed to fetch') {
          console.warn(
            `Failed to [${requestInit.method ?? 'GET'}] URL '${url.toString()}' due to network error.`,
            context,
          )
          return {
            error: ex as Error,
            hasError: true,
            didAbort: false,
            ...didTimeoutFlag,
          }
        }
        // https://developers.google.com/web/updates/2017/09/abortable-fetch#reacting_to_an_aborted_fetch
        if ((ex as Error).name === 'AbortError') {
          // Workaround for Uncaught `Error: The error you provided does not contain a stack trace`
          // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions -- This assignment is ok
          (ex as Error).stack = (ex as Error).stack || 'fake--not-an-actual-stack-trace'
          return {
            error: ex as Error,
            hasError: true,
            didAbort: !didTimeoutFlag.didTimeout,
            ...didTimeoutFlag,
          }
        }
      }
      return {
        response: responseOrError as Response,
        hasError: false,
        didAbort: false,
        ...didTimeoutFlag,
      }
    },
    async handlePossibleResponseFailure({
      fetchResult,
      fetchOperationName,
      context = {},
    }: HandleFailedResponseParams): Promise<boolean> {
      const didNetworkRequestSucceed = self._handleAbortOrNetworkFailure({ fetchResult, fetchOperationName, context })
      if (!didNetworkRequestSucceed) {
        return false
      }
      return await self._handleResponsePayloadFailure({ fetchResult, fetchOperationName, context })
    },
  }))
  .actions((self) => ({
    async loadObjectOrError<T>(
      url: RequestInfo,
      requestInit: FetchWithTimeoutAndAbortControllerOptions,
      _loggingInfo: FetchLoggingParams,
    ): Promise<LoadObjectResult<T>> {
      const fetchResult = await self.fetch(url, requestInit)
      // const didSucceed = await self.handlePossibleResponseFailure({ fetchResult, ...loggingInfo })

      const { response, error, didTimeout } = fetchResult

      if (response?.ok) {
        const text = await response.text()
        const result = text ? { result: JSON.parse(text) as T } : { result: text as unknown as T }
        return result
      } else {
        if (didTimeout) {
          return { error: { ...error, name: 'TimeoutError', message: 'The request timed out' } }
        }
        if (error) {
          return { error: { ...error, name: 'NetworkError', message: 'Network error' } }
        } 
        if (response?.status === StatusCodes.UNAUTHORIZED) {
          loginLink.replace()
          return {
            error: {
              name: 'UnauthorizedError',
              message: 'User unauthorized, probably not logged in',
            },
          }
        }

        const responseText = await fetchResult.response?.text()
        
        let serverErrorMessage: string | null = null
        if (responseText) {
            const responseObject = JSON.parse(responseText) as { message?: string, error?: string, extra?: unknown }
            // Try to extract error message from response using `message` and `extra` fields
            serverErrorMessage = responseObject.message || responseObject.error || null
            if (responseObject.message && responseObject.extra) {
              try {
                serverErrorMessage = MeasurementUnitsService.formatServerResponse(responseObject.message, responseObject.extra)
              } catch(err) {
                console.warn('Failed to format server response, keeping message unformatted', err)
              }
            }
        }
        return {
          error: {
            name: 'BadResponseError',
            message: serverErrorMessage || responseText || fetchResult.response?.statusText || 'Unknown error',
          },
        }
      }
    },
  }))

